import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "../../../environments/env";

declare global {
  interface Window {
    ttq: any;
  }
}

export interface TikTokContent {
  content_id: string;
  content_type: "product" | "product_group";
  content_name: string;
}

export interface TikTokTrackingOptions {
  contents?: TikTokContent[];
  value?: number;
  currency?: string;
}

export interface TikTokIdentifyData {
  email?: string;
  phone_number?: string;
  external_id?: string;
}

@Injectable({
  providedIn: "root",
})
export class TiktokAnalyticsService {
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {}

  /**
   * Verifica si TikTok Pixel está disponible.
   * En SSR/prerender siempre retorna false silenciosamente (sin loggear).
   */
  private isTikTokAvailable(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return !!window.ttq && typeof window.ttq.track === "function";
  }

  /** Log "no disponible" solo en browser — evita spam durante prerender. */
  private warnNotAvailable(): void {
    if (isPlatformBrowser(this.platformId)) {
      console.warn("TikTok Pixel no está disponible");
    }
  }

  /**
   * Hash de datos PII con SHA-256 via SubtleCrypto (Web Crypto API)
   */
  private async hashData(data: string): Promise<string> {
    const normalized = data.toLowerCase().trim();
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(normalized);
    const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  /**
   * Identifica al usuario con datos PII hasheados (async — fire-and-forget)
   */
  async identify(data: TikTokIdentifyData): Promise<void> {
    if (!this.isTikTokAvailable()) {
      this.warnNotAvailable();
      return;
    }

    if (!environment.tiktokTrackingEnabled) {
      return;
    }

    const identifyPayload: any = {};

    const hashPromises: Promise<void>[] = [];

    if (data.email) {
      hashPromises.push(
        this.hashData(data.email).then((h) => {
          identifyPayload.email = h;
        }),
      );
    }

    if (data.phone_number) {
      hashPromises.push(
        this.hashData(data.phone_number).then((h) => {
          identifyPayload.phone_number = h;
        }),
      );
    }

    if (data.external_id) {
      hashPromises.push(
        this.hashData(data.external_id).then((h) => {
          identifyPayload.external_id = h;
        }),
      );
    }

    await Promise.all(hashPromises);

    try {
      window.ttq.identify(identifyPayload);
      console.log("TikTok Identify enviado:", identifyPayload);
    } catch (error) {
      console.error("Error al enviar TikTok Identify:", error);
    }
  }

  /**
   * Envía evento ViewContent - cuando un usuario ve una página de producto
   */
  trackViewContent(options: TikTokTrackingOptions): void {
    if (!this.isTikTokAvailable()) {
      this.warnNotAvailable();
      return;
    }

    if (!environment.tiktokTrackingEnabled) {
      return;
    }

    const payload: any = {};

    if (options.contents) {
      payload.contents = options.contents;
    }

    if (options.value !== undefined) {
      payload.value = options.value;
    }

    if (options.currency) {
      payload.currency = options.currency;
    }

    try {
      window.ttq.track("ViewContent", payload);
      console.log("TikTok ViewContent enviado:", payload);
    } catch (error) {
      console.error("Error al enviar TikTok ViewContent:", error);
    }
  }

  /**
   * Envía evento AddToCart - cuando un usuario añade un producto al carrito
   */
  trackAddToCart(options: TikTokTrackingOptions): void {
    if (!this.isTikTokAvailable()) {
      this.warnNotAvailable();
      return;
    }

    if (!environment.tiktokTrackingEnabled) {
      return;
    }

    const payload: any = {};

    if (options.contents) {
      payload.contents = options.contents;
    }

    if (options.value !== undefined) {
      payload.value = options.value;
    }

    if (options.currency) {
      payload.currency = options.currency;
    }

    try {
      window.ttq.track("AddToCart", payload);
      console.log("TikTok AddToCart enviado:", payload);
    } catch (error) {
      console.error("Error al enviar TikTok AddToCart:", error);
    }
  }

  /**
   * Envía evento InitiateCheckout - cuando un usuario inicia el proceso de checkout
   */
  trackInitiateCheckout(options: TikTokTrackingOptions): void {
    if (!this.isTikTokAvailable()) {
      this.warnNotAvailable();
      return;
    }

    if (!environment.tiktokTrackingEnabled) {
      return;
    }

    const payload: any = {};

    if (options.contents) {
      payload.contents = options.contents;
    }

    if (options.value !== undefined) {
      payload.value = options.value;
    }

    if (options.currency) {
      payload.currency = options.currency;
    }

    try {
      window.ttq.track("InitiateCheckout", payload);
      console.log("TikTok InitiateCheckout enviado:", payload);
    } catch (error) {
      console.error("Error al enviar TikTok InitiateCheckout:", error);
    }
  }

  /**
   * Envía evento CompleteRegistration - cuando un usuario completa el registro
   */
  trackCompleteRegistration(options: TikTokTrackingOptions): void {
    if (!this.isTikTokAvailable()) {
      this.warnNotAvailable();
      return;
    }

    if (!environment.tiktokTrackingEnabled) {
      return;
    }

    const payload: any = {};

    if (options.contents) {
      payload.contents = options.contents;
    }

    if (options.value !== undefined) {
      payload.value = options.value;
    }

    if (options.currency) {
      payload.currency = options.currency;
    }

    try {
      window.ttq.track("CompleteRegistration", payload);
      console.log("TikTok CompleteRegistration enviado:", payload);
    } catch (error) {
      console.error("Error al enviar TikTok CompleteRegistration:", error);
    }
  }

  /**
   * Envía evento Purchase - cuando un usuario completa una compra
   */
  trackPurchase(options: TikTokTrackingOptions): void {
    if (!this.isTikTokAvailable()) {
      this.warnNotAvailable();
      return;
    }

    if (!environment.tiktokTrackingEnabled) {
      return;
    }

    const payload: any = {};

    if (options.contents) {
      payload.contents = options.contents;
    }

    if (options.value !== undefined) {
      payload.value = options.value;
    }

    if (options.currency) {
      payload.currency = options.currency;
    }

    try {
      window.ttq.track("Purchase", payload);
      console.log("TikTok Purchase enviado:", payload);
    } catch (error) {
      console.error("Error al enviar TikTok Purchase:", error);
    }
  }

  /**
   * Método genérico para enviar cualquier evento personalizado
   */
  trackCustomEvent(eventName: string, options: TikTokTrackingOptions): void {
    if (!this.isTikTokAvailable()) {
      this.warnNotAvailable();
      return;
    }

    if (!environment.tiktokTrackingEnabled) {
      return;
    }

    const payload: any = {};

    if (options.contents) {
      payload.contents = options.contents;
    }

    if (options.value !== undefined) {
      payload.value = options.value;
    }

    if (options.currency) {
      payload.currency = options.currency;
    }

    try {
      window.ttq.track(eventName, payload);
      console.log(`TikTok ${eventName} enviado:`, payload);
    } catch (error) {
      console.error(`Error al enviar TikTok ${eventName}:`, error);
    }
  }
}
